import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, Search, Filter, Edit, Copy, Trash2, Eye, Loader2,
  FileText, Clock, TrendingUp, Archive
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScenarioManager = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchScenarios();
  }, [statusFilter]);

  const fetchScenarios = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API}/admin/scenarios?status_filter=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScenarios(response.data.scenarios);
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
      toast.error('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (scenarioId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/admin/scenarios/${scenarioId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scenario duplicated successfully');
      fetchScenarios();
    } catch (error) {
      console.error('Failed to duplicate:', error);
      toast.error('Failed to duplicate scenario');
    }
  };

  const handleArchive = async (scenarioId) => {
    if (!confirm('Archive this scenario? It will be hidden from users.')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/scenarios/${scenarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scenario archived');
      fetchScenarios();
    } catch (error) {
      console.error('Failed to archive:', error);
      toast.error('Failed to archive scenario');
    }
  };

  const handlePublish = async (scenarioId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/admin/scenarios/${scenarioId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scenario published!');
      fetchScenarios();
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish scenario');
    }
  };

  const filteredScenarios = scenarios.filter(scenario =>
    scenario.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    scenario.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-amber-100 text-amber-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || badges.draft;
  };

  const stats = {
    total: scenarios.length,
    published: scenarios.filter(s => s.status !== 'archived').length,
    draft: scenarios.filter(s => s.status === 'draft').length,
    archived: scenarios.filter(s => s.status === 'archived').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Scenarios
          </h1>
          <p className="text-gray-600">
            Manage learning scenarios and educational content
          </p>
        </div>
        <Button
          onClick={() => navigate('/admin/scenarios/new')}
          className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Scenario
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-ssdp-teal" />
            <span className="text-sm font-medium text-gray-600">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-600">Published</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.published}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-gray-600">Drafts</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.draft}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-600">Archived</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.archived}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search scenarios..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Scenarios Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Scenario
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Difficulty
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Last Edited
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredScenarios.map((scenario) => (
                <motion.tr
                  key={scenario.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={scenario.thumbnail}
                        alt={scenario.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <p className="font-semibold text-gray-900">
                          {scenario.title}
                        </p>
                        <p className="text-sm text-gray-500">
                          {scenario.duration}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">
                      {scenario.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 capitalize">
                      {scenario.difficulty || 'medium'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(scenario.status || 'published')}`}>
                      {scenario.status || 'published'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-700">
                      {scenario.last_edited_at 
                        ? new Date(scenario.last_edited_at).toLocaleDateString()
                        : 'N/A'}
                    </p>
                    {scenario.last_edited_by && (
                      <p className="text-xs text-gray-500">
                        by {scenario.last_edited_by.slice(0, 8)}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/scenarios/${scenario.id}/edit`)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(scenario.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4 text-gray-600" />
                      </button>
                      {scenario.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(scenario.id)}
                          className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 transition-colors"
                        >
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => handleArchive(scenario.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Archive"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredScenarios.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No scenarios found</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchQuery ? 'Try a different search term' : 'Create your first scenario to get started'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioManager;
